// ── PrintReport.js — 자영업자용 한 페이지 인쇄 보고서 ────────────────────────
// window.print() 활용. PDF 저장은 인쇄 다이얼로그에서 "PDF로 저장" 선택.

import React, { useEffect } from 'react';
import { CATEGORY_MAJOR, pickPresumedRate, calcPresumedDeduction } from '../data/taxConstants';
import { computeSummary } from '../utils/taxExport';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function PrintReport({
  transactions = [],
  partners = [],
  periodLabel = '',
  bizName = '',
  annualRevenue = null,
  industry = 'food',
  onClose,
}) {
  const s = computeSummary(transactions);
  const profit = s.salesSupply - s.expenseSupply;
  const profitMargin = s.salesSupply > 0 ? (profit / s.salesSupply) * 100 : 0;
  const qualifiedRatio = s.expenseSupply > 0 ? (s.qualifiedExpense / s.expenseSupply) * 100 : 0;
  const presumedRate = pickPresumedRate({ industry, annualRevenue });
  const presumedDeduction = calcPresumedDeduction(s.presumedSupply, presumedRate);
  const dateStr = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    // 모달 열릴 때 body에 print-mode class 추가 (모달 외부 가리기)
    document.body.classList.add('ddabak-print-mode');
    return () => document.body.classList.remove('ddabak-print-mode');
  }, []);

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: 16, overflow: 'auto',
  };
  const modal = {
    background: '#fff', borderRadius: 14,
    width: 800, maxWidth: '100%',
    boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
    margin: '20px auto',
    overflow: 'hidden',
  };
  const header = {
    padding: '12px 18px', borderBottom: '1px solid #eee',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#f9fafb',
  };

  // ── 인쇄 영역 (실제 출력될 내용) ──
  const PrintBody = () => (
    <div className="ddabak-print-area" style={{ padding: '32px 36px', background: '#fff', color: '#222' }}>
      {/* 보고서 헤더 */}
      <div style={{ borderBottom: '3px double #FF6F0F', paddingBottom: 14, marginBottom: 22 }}>
        <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>셈핏 (semfit) 운영 보고서</div>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: '4px 0 6px', color: '#18181B' }}>
          매장 운영 요약 · {periodLabel}
        </h1>
        <div style={{ fontSize: 12, color: '#666' }}>
          {bizName ? `${bizName}` : '사업자명 미입력'} · 발행일 {dateStr} · 거래 {transactions.length}건
        </div>
      </div>

      {/* KPI 4개 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 22 }}>
        <KPIBox label="매출 (공급가액)" value={fmt(s.salesSupply)} suffix="원" color="#FF6F0F" />
        <KPIBox label="매입 (공급가액)" value={fmt(s.expenseSupply)} suffix="원" color="#71717A" />
        <KPIBox label="이익" value={fmt(profit)} suffix="원" color={profit >= 0 ? '#FF6F0F' : '#71717A'}
          sub={s.salesSupply > 0 ? `이익률 ${profitMargin.toFixed(1)}%` : '-'} />
        <KPIBox label="적격증빙 비율" value={qualifiedRatio.toFixed(0)} suffix="%" color="#3b82f6"
          sub={s.unqualifiedExpense > 0 ? `비적격 ${fmt(s.unqualifiedExpense)}원` : '전부 적격'} />
      </div>

      {/* 의제매입공제 (음식점 위주) */}
      {s.presumedSupply > 0 && (
        <div style={{
          padding: 16, marginBottom: 22, borderRadius: 10,
          background: '#FAFAFA',
          border: '1px solid #FED7AA',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9A3412', letterSpacing: 0.5 }}>
            ✨ 의제매입세액공제 추정
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#EA580C', marginTop: 4 }}>
            약 {fmt(presumedDeduction)}원 환급 가능
          </div>
          <div style={{ fontSize: 11, color: '#047857', marginTop: 4 }}>
            의제매입대상 매입가 {fmt(s.presumedSupply)}원 × {presumedRate.label}
          </div>
        </div>
      )}

      {/* 부가세 추정 */}
      <div style={{ marginBottom: 22 }}>
        <SectionTitle>📋 부가세 추정 (참고용)</SectionTitle>
        <Row label="매출 부가세" value={`+${fmt(s.salesVAT)}원`} />
        <Row label="매입세액공제" value={`-${fmt(s.deductibleVAT)}원`} />
        <Row label={`의제매입공제 (${presumedRate.numerator}/${presumedRate.denominator})`} value={`-${fmt(presumedDeduction)}원`} />
        <Row label="납부(환급) 추정" value={fmt(s.salesVAT - s.deductibleVAT - presumedDeduction) + '원'} bold />
        <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>
          ※ 이 수치는 자동 분류 기준 추정. 실제 신고는 세무사·홈택스에서 최종 확정.
        </div>
      </div>

      {/* 카테고리별 매입 */}
      {Object.keys(s.byMajor).length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <SectionTitle>🏷 카테고리별 매입</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {CATEGORY_MAJOR.filter(m => (s.byMajor[m.id] || 0) > 0).map(m => {
              const v = s.byMajor[m.id] || 0;
              const pct = s.expenseSupply > 0 ? (v / s.expenseSupply) * 100 : 0;
              return (
                <div key={m.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                    <span>{m.emoji} {m.label}</span>
                    <span style={{ color: '#666' }}>{fmt(v)}원 ({pct.toFixed(1)}%)</span>
                  </div>
                  <div style={{ background: '#f3f4f6', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ background: m.color, height: '100%', width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 적격증빙 경고 */}
      {s.unqualifiedExpense > 0 && (
        <div style={{
          padding: 12, marginBottom: 22, borderRadius: 8,
          background: '#F4F4F5', border: '1px solid #D4D4D8',
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#52525B', marginBottom: 4 }}>
            ⚠ 비적격증빙 매입 {fmt(s.unqualifiedExpense)}원
          </div>
          <div style={{ fontSize: 11, color: '#78350f' }}>
            가산세 위험 추정 약 {fmt(s.unqualifiedExpense * 0.02)}원 (2%). 가능하면 세금계산서·카드·현금영수증으로 거래.
          </div>
        </div>
      )}

      {/* 푸터 */}
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginTop: 24, fontSize: 10, color: '#999' }}>
        본 보고서는 셈핏 (semfit)에서 자동 생성. 신고 시 최종 검토는 세무사 또는 본인 책임.
      </div>
    </div>
  );

  return (
    <>
      <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
        <div style={modal} className="ddabak-print-modal">
          <div style={header} className="ddabak-print-hide">
            <div style={{ fontWeight: 700, fontSize: 15 }}>📊 운영 보고서 미리보기</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => window.print()}
                style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: '#FF6F0F', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                🖨 인쇄 / PDF 저장
              </button>
              <button onClick={onClose}
                style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid #ddd', background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' }}>
                닫기
              </button>
            </div>
          </div>
          <PrintBody />
          <div className="ddabak-print-hide" style={{ padding: '10px 18px 14px', fontSize: 11, color: '#888' }}>
            💡 인쇄 다이얼로그에서 <strong>"대상: PDF로 저장"</strong>을 선택하면 PDF 파일로 받을 수 있습니다.
          </div>
        </div>
      </div>

      {/* 인쇄 시 적용되는 CSS — 모달 chrome은 숨기고 본문만 인쇄 */}
      <style>{`
        @media print {
          body.ddabak-print-mode > div:not(.ddabak-print-modal-root) { display: none !important; }
          .ddabak-print-hide { display: none !important; }
          .ddabak-print-modal { box-shadow: none !important; border-radius: 0 !important; margin: 0 !important; width: 100% !important; max-width: none !important; }
          .ddabak-print-area { padding: 16px !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </>
  );
}

// ── 작은 컴포넌트 ──
function KPIBox({ label, value, suffix, color, sub }) {
  return (
    <div style={{
      padding: 12, borderRadius: 8, background: '#fff',
      border: '1px solid #e5e7eb',
    }}>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: color || '#222', marginTop: 4 }}>
        {value}<span style={{ fontSize: 12, fontWeight: 600, marginLeft: 2, color: '#666' }}>{suffix}</span>
      </div>
      {sub && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 700, color: '#374151',
      borderBottom: '2px solid #FF6F0F', paddingBottom: 4, marginBottom: 10,
    }}>{children}</div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between',
                  padding: '5px 0', borderBottom: '1px dotted #e5e7eb',
                  fontSize: bold ? 14 : 12, fontWeight: bold ? 700 : 400,
                  color: bold ? '#FF6F0F' : '#444' }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
